import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import MetricCard from "../components/MetricCard";
import LoadingSpinner from "../components/LoadingSpinner";
import { SkeletonCard, SkeletonTable } from "../components/SkeletonLoader";
import EmptyState from "../components/EmptyState";
import ParseHealthCard from "../components/ParseHealthCard";
import DealScoreCard from "../components/DealScoreCard";
import DataQualityReport from "../components/DataQualityReport";
import { BarChart3, Printer, Upload, DollarSign, FileDown, ChevronRight, RefreshCw } from "lucide-react";
import { useDealContext } from "../hooks/useDealContext";
import { friendlyError } from "../utils/errors";
import {
  fetchDeal,
  fetchScenarios,
  fetchDocuments,
  autoProject,
  enhanceDocument,
  parseDocument,
  fetchDocument,
  updateDealAssumptions,
  updateDeal,
  downloadPdfReport,
  type ScenarioResponse,
  type ProjectionResult,
  type AnnualCashFlow,
  type DocumentMeta,
} from "../api/client";
import type { Deal } from "../api/types";
import { usePostParse } from "../contexts/PostParseContext";
import {
  fmtCurrency,
  fmtCurrencyAcct,
  fmtCurrencyCompact,
  fmtPSF,
  fmtPercent,
  fmtPercentRaw,
  fmtMultiple,
  fmtDecimal,
  fmtNumber,
  fmtSF,
} from "../utils/format";

const tooltipStyle = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #D4D0C8",
  borderRadius: "8px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
};

export default function DashboardPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal, scenarios: ctxScenarios, setScenarios: setCtxScenarios } = useDealContext();
  const { showParseIssues } = usePostParse();
  const [localScenarios, setLocalScenarios] = useState<ScenarioResponse[]>([]);
  const scenarios = ctxScenarios && ctxScenarios.length > 0 ? ctxScenarios : localScenarios;
  const setScenarios = (s: ScenarioResponse[]) => { setLocalScenarios(s); setCtxScenarios?.(s); };
  const [documents, setDocuments] = useState<DocumentMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoProjecting, setAutoProjecting] = useState(false);
  const [autoProjectError, setAutoProjectError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pricing mode: "cap_rate" (default) or "price"
  const [pricingMode, setPricingMode] = useState<"cap_rate" | "price">("cap_rate");
  const [ppInput, setPpInput] = useState("");
  const [capRateInput, setCapRateInput] = useState("7.0");
  const [ppError, setPpError] = useState<string | null>(null);
  const [savingPP, setSavingPP] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const loadAll = useCallback(async () => {
    if (!dealId) return;
    try {
      // Re-fetch deal for fresh property_data
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);

      const [scenarioList, docList] = await Promise.all([
        fetchScenarios(dealId),
        fetchDocuments(dealId),
      ]);
      setScenarios(scenarioList);
      setDocuments(docList);

      // If no scenario has a projection, try auto-projecting
      const hasProjection = scenarioList.some((s) => s.projection_result);
      if (!hasProjection && (freshDeal.property_data || docList.some((d) => d.parse_status === "complete"))) {
        setAutoProjecting(true);
        try {
          const projResult = await autoProject(dealId);
          if (projResult.status === "error") {
            setAutoProjectError((projResult as any).error || "Property model could not be generated.");
          } else {
            const updated = await fetchScenarios(dealId);
            setScenarios(updated);
            // Refresh deal to get newly-built property_data
            const refreshedDeal = await fetchDeal(dealId);
            setDeal(refreshedDeal);
          }
        } catch (err: any) {
          const msg = err?.response?.data?.detail || err?.message || "Auto-project failed";
          setAutoProjectError(friendlyError(msg));
        } finally {
          setAutoProjecting(false);
        }
      }
    } catch (e) {
      setError(friendlyError(e instanceof Error ? e.message : "Failed to load dashboard"));
    } finally {
      setLoading(false);
    }
  }, [dealId, setDeal]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleRecalculate = useCallback(async () => {
    if (!dealId) return;
    setRecalculating(true);
    try {
      await autoProject(dealId);
      const [refreshedDeal, updatedScenarios] = await Promise.all([
        fetchDeal(dealId),
        fetchScenarios(dealId),
      ]);
      setDeal(refreshedDeal);
      setScenarios(updatedScenarios);
    } catch (e: any) {
      setError(friendlyError(e?.message || "Recalculation failed"));
    } finally {
      setRecalculating(false);
    }
  }, [dealId, setDeal]);

  const handleSavePurchasePrice = async (directPrice?: number) => {
    if (!dealId) return;
    const pp = directPrice ?? parseFloat(ppInput.replace(/[,$\s]/g, ""));
    if (isNaN(pp) || pp <= 0) {
      setPpError("Enter a positive number");
      return;
    }
    setPpError(null);
    setSavingPP(true);
    try {
      await updateDealAssumptions(dealId, { purchase_price: pp });
      // Refresh everything
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal as Deal);
      const updatedScenarios = await fetchScenarios(dealId);
      setScenarios(updatedScenarios);
      setPpInput("");
    } catch (e: any) {
      const detail = e?.message || "Failed to save purchase price";
      setError(friendlyError(detail));
    } finally {
      setSavingPP(false);
    }
  };

  if (loading || autoProjecting) {
    return (
      <div className="space-y-6 animate-fade-in">
        {autoProjecting && (
          <p className="text-sm text-bark-400 animate-pulse text-center">Generating projections...</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <SkeletonTable rows={8} cols={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <p className="text-red-700 font-medium mb-2">Failed to load dashboard</p>
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  const baseScenario = scenarios.find((s) => s.projection_result);
  const projection: ProjectionResult | null =
    baseScenario?.projection_result ?? null;

  // New Deal Wizard: show only when NO documents have been uploaded
  const hasDocuments = documents && documents.length > 0;
  if (!projection && !hasDocuments) {
    return (
      <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
        <div className="text-center pt-8">
          <h2 className="font-display text-2xl font-semibold text-bark-800">Welcome to {deal.name}</h2>
          <p className="text-sm text-bark-400 mt-2">Get started by uploading your documents</p>
        </div>
        <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-8 h-8 rounded-full bg-honey-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-honey-700 font-bold text-sm">1</span>
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-bark-800">Upload Documents</h3>
              <p className="text-sm text-bark-400 mt-1">Upload your rent roll and operating statement to get started. The system will automatically parse and build your cash flow model.</p>
            </div>
          </div>
          <Link
            to={`/deals/${dealId}/documents`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md w-full justify-center"
          >
            <Upload className="w-4 h-4" />
            Upload Documents
          </Link>
        </div>
      </div>
    );
  }

  // Documents exist but property_data not yet built — show building state or error
  if (!projection && hasDocuments && !deal.property_data) {
    if (autoProjectError) {
      return (
        <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
          <ParseHealthCard
            documents={documents}
            deal={deal}
            scenarios={scenarios}
            dealId={dealId!}
            onEnhance={async (docId) => {
              await enhanceDocument(dealId!, docId);
              const refreshedDocs = await fetchDocuments(dealId!);
              setDocuments(refreshedDocs);
            }}
            onReparse={async () => {
              for (const doc of documents) {
                if (doc.parse_status === "complete" || doc.parse_status === "error") {
                  await parseDocument(dealId!, doc.id);
                  // Check for parse issues
                  try {
                    const updated = await fetchDocument(dealId!, doc.id);
                    const pr = updated.parse_result as Record<string, unknown> | null;
                    if (pr) {
                      const issues: Array<{ severity: "critical" | "warning"; message: string; action?: { label: string; path: string } }> = [];
                      const conf = (pr.confidence_score ?? pr.confidence ?? 100) as number;
                      if (conf < 50) issues.push({ severity: "critical", message: `Low confidence (${fmtPercentRaw(conf, 0)}) on "${doc.filename}".`, action: { label: "Verify fields", path: "verify" } });
                      if (issues.length > 0) showParseIssues(dealId!, issues);
                    }
                  } catch {}
                }
              }
              setAutoProjectError(null);
              setLoading(true);
              loadAll();
            }}
          />
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h3 className="font-display text-base font-semibold text-bark-800 mb-2">Property model could not be generated</h3>
            <p className="text-sm text-red-600 mb-4">{autoProjectError}</p>
            <p className="text-sm text-bark-400 mb-4">Check the document parse results on the Documents tab for errors or missing data.</p>
            <div className="flex gap-3">
              <Link
                to={`/deals/${dealId}/documents`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 transition-all"
              >
                <Upload className="w-4 h-4" />
                Review Documents
              </Link>
              <button
                onClick={() => {
                  setAutoProjectError(null);
                  setLoading(true);
                  loadAll();
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-bark-700 text-sm font-semibold rounded-lg border border-grove-300 hover:bg-grove-50 transition-all"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 animate-fade-in">
        <LoadingSpinner />
        <p className="text-sm text-bark-400 animate-pulse">Building property model from uploaded documents...</p>
        <p className="text-xs text-bark-300">This usually takes a few seconds</p>
      </div>
    );
  }

  if (!projection && hasDocuments) {
    // Documents exist but projection failed — show diagnostic message
    const hasRR = documents.some((d) => d.file_type === "rent_roll" && d.parse_status === "complete");
    const hasOS = documents.some((d) => d.file_type === "operating_statement" && d.parse_status === "complete");
    const failedScenario = scenarios.find((s) => s.projection_result === null || (s as any).error);
    return (
      <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
        <div className="bg-honey-50 border border-honey-200 rounded-xl p-6">
          <h3 className="font-display text-base font-semibold text-bark-800 mb-2">Projection could not be generated</h3>
          <ul className="text-sm text-bark-500 space-y-1 mb-4">
            <li>{hasRR ? "\u2705" : "\u274C"} Rent roll {hasRR ? "parsed" : "not yet parsed"}</li>
            <li>{hasOS ? "\u2705" : "\u274C"} Operating statement {hasOS ? "parsed" : "not yet parsed"}</li>
          </ul>
          {!hasRR && (
            <p className="text-sm text-bark-400 mb-3">Upload a rent roll to generate projections.</p>
          )}
          {hasRR && !hasOS && (
            <p className="text-sm text-bark-400 mb-3">Upload an operating statement for a complete projection.</p>
          )}
          {hasRR && hasOS && (
            <p className="text-sm text-bark-400 mb-3">Both documents are parsed but the projection engine encountered an error. Try re-uploading or editing tenant data on the Rent Roll tab.</p>
          )}
          <Link
            to={`/deals/${dealId}/documents`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 transition-all"
          >
            <Upload className="w-4 h-4" />
            {hasRR && hasOS ? "Review Documents" : "Upload Documents"}
          </Link>
        </div>
      </div>
    );
  }

  if (!projection) {
    return (
      <div className="space-y-6">
        <EmptyState
          icon={BarChart3}
          title="No projections yet"
          description="Upload a rent roll and operating statement to generate projections automatically."
          action={
            <Link
              to={`/deals/${dealId}/documents`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
            >
              <Upload className="w-4 h-4" />
              Upload Documents
            </Link>
          }
        />
      </div>
    );
  }

  const { annual_cash_flows: cfs, return_metrics: rm } = projection;
  const prop = deal.property_data;
  const tla = prop?.total_leasable_area ?? 1;
  const inPlaceNOI = (projection as any).in_place_noi as number | undefined;
  const purchasePrice = prop?.purchase_price ?? 0;
  const hasReturnMetrics = purchasePrice > 0 && rm.irr_unlevered != null;

  // Prefer the stored deal cap rate over the projection's computed value.
  // When the user sets a target cap via chat, the backend derives purchase
  // price from NOI/cap; the projection then recomputes cap using Year 1
  // NOI (which bakes in escalations, vacancy, and mgmt fee growth) and
  // can diverge by 100-150bps from the user's input. If the stored value
  // is missing, fall back to in-place NOI / purchase price — NEVER to
  // rm.going_in_cap_rate, which has been observed returning the drifted
  // Year-1 value despite the engine fix at engine/returns.py:96.
  const goingInCapDisplay = (() => {
    if (prop?.going_in_cap_rate && prop.going_in_cap_rate > 0) {
      return prop.going_in_cap_rate;
    }
    const ipNoi = (projection as any).in_place_noi as number | undefined;
    if (ipNoi && purchasePrice > 0) return ipNoi / purchasePrice;
    return hasReturnMetrics ? rm.going_in_cap_rate : 0;
  })();

  // Prompt 74 moved exit_cap_rate from deal.property_data to
  // scenario.assumptions. Read from the scenario first; fall back to the
  // (now-stale) deal value only if the scenario hasn't set one.
  const exitCapDisplay =
    (baseScenario?.assumptions as any)?.exit_cap_rate
      ?? prop?.exit_cap_rate
      ?? 0;

  const yr1 = cfs[0];
  const lastYr = cfs[cfs.length - 1];
  const tenants = prop?.tenants ?? [];
  const occupiedSF = tenants.reduce((s: number, t: any) => s + (t.leased_sf || 0), 0);
  const occupancy = tla > 0 ? occupiedSF / tla : null;

  // Projected Year 1 occupancy: use actual occupied SF / total leasable area
  // Don't derive from PGI (which ignores vacant space entirely)
  const projectedOccupancy = occupancy != null
    ? Math.min(occupancy * (1 - (prop?.general_vacancy_pct ?? 0.05)), 1.0)
    : null;

  // WALT: Weighted Average Lease Term
  // Use effective analysis start date: scenario override > property_data > today
  const effectiveStartStr = baseScenario?.assumptions?.analysis_start_date || prop?.analysis_start_date || "";
  const now = effectiveStartStr ? new Date(effectiveStartStr as string) : new Date();
  const walt = occupiedSF > 0 ? tenants.reduce((sum: number, t: any) => {
    const expDate = t.lease_expiration_date ? new Date(t.lease_expiration_date) : now;
    const remaining = Math.max(0, (expDate.getTime() - now.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    return sum + (t.leased_sf || 0) * remaining;
  }, 0) / occupiedSF : null;

  // Average in-place rent PSF
  const totalAnnualRent = tenants.reduce((s: number, t: any) => s + (t.base_rent_annual_psf || 0) * (t.leased_sf || 0), 0);
  const avgRentPSF = occupiedSF > 0 ? totalAnnualRent / occupiedSF : null;

  // Largest tenant exposure
  const largestTenant = tenants.length > 0 ? tenants.reduce((best: any, t: any) => (t.leased_sf || 0) > (best.leased_sf || 0) ? t : best, tenants[0]) : null;
  const largestPct = largestTenant && tla > 0 ? (largestTenant.leased_sf || 0) / tla : null;

  // Near-term rollover (within 36 months / 3 years)
  const rolloverSF = tenants.reduce((sum: number, t: any) => {
    if (!t.lease_expiration_date) return sum;
    const exp = new Date(t.lease_expiration_date);
    const diffMonths = (exp.getTime() - now.getTime()) / (30.44 * 24 * 60 * 60 * 1000);
    return diffMonths > 0 && diffMonths <= 36 ? sum + (t.leased_sf || 0) : sum;
  }, 0);
  const rolloverPct = tla > 0 ? rolloverSF / tla : null;

  const chartData = cfs.map((cf: AnnualCashFlow) => ({
    year: `Yr ${cf.year}`,
    calendarYear: cf.calendar_year,
    noi: Math.round(cf.net_operating_income),
    cfBeforeDebt: Math.round(cf.cash_flow_before_debt),
    cfAfterDebt: Math.round(cf.cash_flow_after_debt),
    debtService: Math.round(cf.debt_service),
  }));

  const currencyTooltip = (value: number | undefined) =>
    value != null ? fmtCurrency(value) : "--";

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Print header */}
      <div className="print-header hidden print:block">
        <h1>{deal.name || "Dashboard"}</h1>
        <p>{deal.address || ""} &middot; Investment Summary</p>
      </div>

      {/* Parse Health Card */}
      {hasDocuments && (
        <ParseHealthCard
          documents={documents}
          deal={deal}
          scenarios={scenarios}
          dealId={dealId!}
          onEnhance={async (docId) => {
            await enhanceDocument(dealId!, docId);
            const refreshedDocs = await fetchDocuments(dealId!);
            setDocuments(refreshedDocs);
          }}
          onReparse={async () => {
            for (const doc of documents) {
              if (doc.parse_status === "complete" || doc.parse_status === "error") {
                await parseDocument(dealId!, doc.id);
                try {
                  const updated = await fetchDocument(dealId!, doc.id);
                  const pr = updated.parse_result as Record<string, unknown> | null;
                  if (pr) {
                    const issues: Array<{ severity: "critical" | "warning"; message: string; action?: { label: string; path: string } }> = [];
                    const conf = (pr.confidence_score ?? pr.confidence ?? 100) as number;
                    if (conf < 50) issues.push({ severity: "critical", message: `Low confidence (${fmtPercentRaw(conf, 0)}) on "${doc.filename}".`, action: { label: "Verify fields", path: "verify" } });
                    if (issues.length > 0) showParseIssues(dealId!, issues);
                  }
                } catch {}
              }
            }
            loadAll();
          }}
        />
      )}

      {/* Data Quality Report */}
      {hasDocuments && deal && (
        <DataQualityReport deal={deal} dealId={dealId!} documents={documents} onResolved={loadAll} />
      )}

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-bark-800">Investment Summary</h2>
          <p className="text-sm text-bark-400 mt-1">Key metrics and cash flow projections</p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <select
            value={deal.status || "draft"}
            onChange={async (e) => {
              if (!dealId) return;
              try {
                const updated = await updateDeal(dealId, { status: e.target.value });
                setDeal(updated as Deal);
              } catch { /* ignore */ }
            }}
            className="px-3 py-2 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="under_loi">Under LOI</option>
            <option value="under_contract">Under Contract</option>
            <option value="closed">Closed</option>
            <option value="dead">Dead</option>
          </select>
          <button
            onClick={async () => {
              setDownloadingPdf(true);
              try { await downloadPdfReport(dealId!); }
              catch (e) { console.error("PDF generation failed:", e); }
              finally { setDownloadingPdf(false); }
            }}
            disabled={downloadingPdf}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-forest-700 rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-all"
          >
            <FileDown className="w-4 h-4" />
            {downloadingPdf ? "Generating..." : "Download Summary"}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {/* Pricing input — cap rate (default) or direct purchase price */}
      {purchasePrice <= 0 && (
        <div className="bg-honey-50 border border-honey-200 rounded-xl p-5 print:hidden">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-honey-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-honey-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-bark-700">Set purchase price to see return metrics</p>
                <p className="text-xs text-bark-400 mt-0.5">
                  {inPlaceNOI != null && inPlaceNOI > 0
                    ? `In Place NOI: ${fmtCurrency(inPlaceNOI)} (${fmtPSF(inPlaceNOI / tla)})`
                    : "IRR, cap rates, and equity multiple require a purchase price."}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Toggle */}
              <div className="flex bg-grove-200 rounded-lg p-0.5">
                <button
                  onClick={() => setPricingMode("cap_rate")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    pricingMode === "cap_rate"
                      ? "bg-white text-bark-700 shadow-sm"
                      : "text-bark-400 hover:text-bark-600"
                  }`}
                >
                  Cap Rate
                </button>
                <button
                  onClick={() => setPricingMode("price")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    pricingMode === "price"
                      ? "bg-white text-bark-700 shadow-sm"
                      : "text-bark-400 hover:text-bark-600"
                  }`}
                >
                  Price
                </button>
              </div>

              {pricingMode === "cap_rate" ? (
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <input
                      type="number"
                      step="0.25"
                      min="1"
                      max="20"
                      value={capRateInput}
                      onChange={(e) => setCapRateInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const cr = parseFloat(capRateInput);
                          if (!isNaN(cr) && cr > 0 && inPlaceNOI && inPlaceNOI > 0) {
                            const pp = Math.round(inPlaceNOI / (cr / 100));
                            setPpInput(String(pp));
                            handleSavePurchasePrice(pp);
                          } else {
                            setPpError("Enter a valid cap rate");
                          }
                        }
                      }}
                      className="w-20 px-3 py-2 text-sm border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums"
                    />
                  </div>
                  <span className="text-sm text-bark-500">%</span>
                  {inPlaceNOI != null && inPlaceNOI > 0 && (() => {
                    const cr = parseFloat(capRateInput);
                    const impliedPP = !isNaN(cr) && cr > 0 ? Math.round(inPlaceNOI / (cr / 100)) : 0;
                    return impliedPP > 0 ? (
                      <span className="text-xs text-bark-400 ml-1">= {fmtCurrencyCompact(impliedPP)} ({fmtPSF(impliedPP / tla)})</span>
                    ) : null;
                  })()}
                  <button
                    onClick={() => {
                      const cr = parseFloat(capRateInput);
                      if (!isNaN(cr) && cr > 0 && inPlaceNOI && inPlaceNOI > 0) {
                        const pp = Math.round(inPlaceNOI / (cr / 100));
                        handleSavePurchasePrice(pp);
                      } else {
                        setPpError(inPlaceNOI == null || inPlaceNOI <= 0 ? "No In Place NOI available" : "Enter a valid cap rate");
                      }
                    }}
                    disabled={savingPP}
                    className="px-4 py-2 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all"
                  >
                    {savingPP ? "Saving..." : "Apply"}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-bark-500">$</span>
                  <div className="flex flex-col">
                    <input
                      type="text"
                      value={ppInput}
                      onChange={(e) => {
                        setPpInput(e.target.value);
                        if (ppError) {
                          const val = parseFloat(e.target.value.replace(/[,$\s]/g, ""));
                          if (!isNaN(val) && val > 0) setPpError(null);
                        }
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handleSavePurchasePrice()}
                      placeholder="e.g. 5,000,000"
                      className={`w-40 px-3 py-2 text-sm border rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-2 tabular-nums ${
                        ppError
                          ? "border-red-500 focus:ring-red-400 focus:border-red-400"
                          : "border-grove-300 focus:ring-honey-400 focus:border-honey-400"
                      }`}
                    />
                  </div>
                  <button
                    onClick={() => handleSavePurchasePrice()}
                    disabled={savingPP || !ppInput}
                    className="px-4 py-2 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all"
                  >
                    {savingPP ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
              {ppError && (
                <span className="text-xs text-red-600">{ppError}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Returns summary panel */}
      <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
        <div className="px-6 py-5 border-b border-grove-200 flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold text-bark-700">Returns Summary</h3>
          <Link
            to={`/deals/${dealId}/cash-flow`}
            className="text-xs font-medium text-forest-700 hover:text-forest-600 transition-colors"
          >
            Edit Assumptions &rarr;
          </Link>
        </div>
        <div className="p-6 space-y-5">
          {/* Primary returns — grayed out when no purchase price */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger-children">
            <MetricCard label="Unlevered IRR" value={hasReturnMetrics ? fmtPercent(rm.irr_unlevered, 2) : "--"} accent={hasReturnMetrics} />
            <MetricCard label="Levered IRR" value={hasReturnMetrics ? fmtPercent(rm.irr_levered, 2) : "--"} accent={hasReturnMetrics} />
            <MetricCard label="Equity Multiple" value={hasReturnMetrics ? fmtMultiple(rm.equity_multiple) : "--"} accent={hasReturnMetrics} />
            <MetricCard label="Cash-on-Cash" value={hasReturnMetrics ? fmtPercent(rm.avg_cash_on_cash) : "--"} sublabel={hasReturnMetrics ? "Average" : "Needs purchase price"} />
            <MetricCard label="Avg DSCR" value={rm.avg_dscr ? fmtDecimal(rm.avg_dscr) : "--"} sublabel={rm.min_dscr ? `Min: ${fmtDecimal(rm.min_dscr)}` : (!rm.avg_dscr ? "No debt configured" : undefined)} />
            <MetricCard label="NPV" value={hasReturnMetrics ? fmtCurrencyCompact(rm.npv) : "--"} />
          </div>

          {/* Deal assumptions + secondary metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 stagger-children">
            <MetricCard label="Purchase Price" value={purchasePrice > 0 ? fmtCurrencyCompact(purchasePrice) : "--"} sublabel={purchasePrice > 0 ? fmtPSF(purchasePrice / tla) : "Not set"} />
            <MetricCard label="Going-In Cap" value={fmtPercent(goingInCapDisplay, 2)} />
            <MetricCard label="Exit Cap Rate" value={exitCapDisplay > 0 ? fmtPercent(exitCapDisplay, 2) : "Not set"} />
            <MetricCard label="Exit Price" value={fmtCurrencyCompact(rm.exit_price)} sublabel={rm.net_exit_proceeds != null ? `Net: ${fmtCurrencyCompact(rm.net_exit_proceeds)}` : rm.exit_price ? fmtPSF(rm.exit_price / tla) : undefined} />
            <MetricCard label="Hold Period" value={prop ? `${prop.hold_period_years} yrs` : "--"} />
            <MetricCard label="Discount Rate" value={fmtPercent((baseScenario?.assumptions as any)?.discount_rate ?? prop?.discount_rate, 2)} />
          </div>
        </div>
      </div>

      {/* Analysis Period Context */}
      {effectiveStartStr && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 bg-grove-50 border border-grove-200 rounded-lg text-xs text-bark-500">
          <span>Analysis: <strong className="text-bark-700">
            {(() => {
              const startYear = now.getFullYear();
              const holdYears = (baseScenario?.assumptions as any)?.hold_period_years ?? prop?.hold_period_years ?? 10;
              return `Jan ${startYear} \u2013 Dec ${startYear + holdYears - 1} (${holdYears}-yr hold)`;
            })()}
          </strong></span>
          {(() => {
            const opex = (prop?.operating_expenses as any[]) || [];
            const baseYear = opex.length > 0 ? opex[opex.length - 1]?.year : null;
            const startYear = now.getFullYear();
            const growthYrs = baseYear ? Math.max(0, startYear - baseYear) : 0;
            return baseYear ? (
              <span>Base Expense Year: <strong className="text-bark-700">{baseYear}</strong> ({growthYrs} yr{growthYrs !== 1 ? "s" : ""} growth)</span>
            ) : null;
          })()}
          {(() => {
            const yr1Count = tenants.filter((t: any) => {
              if (!t.lease_expiration_date) return false;
              const exp = new Date(t.lease_expiration_date);
              return exp.getFullYear() === now.getFullYear();
            }).length;
            return yr1Count > 0 ? (
              <span className="text-honey-700">{yr1Count} lease{yr1Count > 1 ? "s" : ""} expiring Year 1</span>
            ) : null;
          })()}
        </div>
      )}

      {/* Row 1 — Property Overview */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-bark-400 mb-2">Property Overview</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
          <MetricCard label="GLA" value={fmtSF(tla)} />
          <MetricCard label="Occupancy" value={fmtPercent(occupancy)} sublabel={projectedOccupancy != null ? `Yr 1 Proj: ${fmtPercent(projectedOccupancy)}` : "Current"} />
          <MetricCard label="WALT" value={walt != null ? `${walt.toFixed(1)} yrs` : "--"} sublabel="Years remaining" />
          <MetricCard label="Base Rent PSF" value={fmtPSF(avgRentPSF)} sublabel="Annual, occupied" />
        </div>
      </div>

      {/* Row 2 — Financial Summary */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-bark-400">Financial Summary</p>
          <button
            onClick={handleRecalculate}
            disabled={recalculating}
            title="Recalculate with the latest engine. Use after a deploy that changed the projection formulas."
            className="inline-flex items-center gap-1 text-[10px] font-medium text-bark-400 hover:text-forest-700 transition-colors disabled:opacity-50 print:hidden"
          >
            <RefreshCw className={`w-3 h-3 ${recalculating ? "animate-spin" : ""}`} />
            {recalculating ? "Recalculating…" : "Recalculate"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
          <MetricCard label="In Place NOI" value={fmtCurrencyCompact(inPlaceNOI)} accent sublabel={inPlaceNOI != null ? fmtPSF(inPlaceNOI / tla) : undefined} />
          <MetricCard label="Year 1 NOI" value={fmtCurrencyCompact(yr1?.net_operating_income)} sublabel={fmtPSF(yr1 ? yr1.net_operating_income / tla : null)} />
          <MetricCard label="Going-In Cap" value={fmtPercent(goingInCapDisplay, 2)} />
          <MetricCard label="Purchase Price" value={purchasePrice > 0 ? fmtCurrencyCompact(purchasePrice) : "--"} sublabel={purchasePrice > 0 ? fmtPSF(purchasePrice / tla) : "Not set"} />
        </div>
      </div>

      {/* In Place NOI per-tenant breakdown */}
      {(() => {
        const ipDetails = (projection as any)?.in_place_tenant_details as { tenant_name: string; suite: string; leased_sf: number; rent_psf: number; annual_rent: number; reimbursement: number; total_revenue: number; expense_allocation: number; net_contribution: number; lease_expiry: string | null }[] | undefined;
        if (!ipDetails || ipDetails.length === 0) return null;
        return (
          <details className="group">
            <summary className="text-[10px] font-semibold uppercase tracking-wide text-bark-400 mb-2 cursor-pointer list-none flex items-center gap-1.5">
              <ChevronRight className="w-3 h-3 text-bark-400 transition-transform group-open:rotate-90" />
              In Place NOI — Per-Tenant Breakdown
            </summary>
            <div className="overflow-x-auto bg-white rounded-xl border border-grove-300 shadow-sm">
              <table className="data-table w-full text-[11px]">
                <thead>
                  <tr className="bg-grove-100/60">
                    <th className="text-left px-3 py-2 font-semibold text-bark-500">Tenant</th>
                    <th className="text-left px-3 py-2 font-semibold text-bark-500">Suite</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500">SF</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500">Rent PSF</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500">Annual Rent</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500">Reimb</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500">Revenue</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500">Exp Alloc</th>
                    <th className="num px-3 py-2 font-semibold text-bark-500 text-forest-700">Net Contrib</th>
                  </tr>
                </thead>
                <tbody>
                  {ipDetails.map((t, i) => (
                    <tr key={i} className="border-b border-grove-100 hover:bg-grove-50/40">
                      <td className="px-3 py-1.5 font-medium text-bark-600">{t.tenant_name}</td>
                      <td className="px-3 py-1.5 text-bark-400">{t.suite || "--"}</td>
                      <td className="num px-3 py-1.5">{fmtNumber(t.leased_sf)}</td>
                      <td className="num px-3 py-1.5">{fmtPSF(t.rent_psf)}</td>
                      <td className="num px-3 py-1.5">{fmtCurrency(t.annual_rent)}</td>
                      <td className="num px-3 py-1.5">{fmtCurrency(t.reimbursement)}</td>
                      <td className="num px-3 py-1.5">{fmtCurrency(t.total_revenue)}</td>
                      <td className="num px-3 py-1.5 text-bark-400">{fmtCurrencyAcct(-t.expense_allocation)}</td>
                      <td className="num px-3 py-1.5 font-semibold text-forest-700">{fmtCurrency(t.net_contribution)}</td>
                    </tr>
                  ))}
                  <tr className="bg-grove-100/40 font-semibold">
                    <td className="px-3 py-2 text-bark-700" colSpan={2}>Total</td>
                    <td className="num px-3 py-2">{fmtNumber(ipDetails.reduce((s, t) => s + t.leased_sf, 0))}</td>
                    <td className="num px-3 py-2"></td>
                    <td className="num px-3 py-2">{fmtCurrency(ipDetails.reduce((s, t) => s + t.annual_rent, 0))}</td>
                    <td className="num px-3 py-2">{fmtCurrency(ipDetails.reduce((s, t) => s + t.reimbursement, 0))}</td>
                    <td className="num px-3 py-2">{fmtCurrency(ipDetails.reduce((s, t) => s + t.total_revenue, 0))}</td>
                    <td className="num px-3 py-2 text-bark-400">{fmtCurrencyAcct(-ipDetails.reduce((s, t) => s + t.expense_allocation, 0))}</td>
                    <td className="num px-3 py-2 text-forest-700">{fmtCurrency(ipDetails.reduce((s, t) => s + t.net_contribution, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        );
      })()}

      {/* Row 4 — Risk Indicators */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-bark-400 mb-2">Risk Indicators</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger-children">
          <MetricCard label="Avg DSCR" value={rm.avg_dscr ? fmtDecimal(rm.avg_dscr) : "--"} sublabel={rm.min_dscr ? `Min: ${fmtDecimal(rm.min_dscr)}` : (!rm.avg_dscr ? "No debt configured" : undefined)} />
          <MetricCard label="Near-Term Rollover" value={fmtPercent(rolloverPct)} sublabel="Expires within 3 years" />
          <MetricCard label="Largest Tenant" value={largestPct != null ? fmtPercent(largestPct) : "--"} sublabel={largestTenant ? `${largestTenant.tenant_name}` : undefined} />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-grove-300 p-6 shadow-sm">
          <h3 className="font-display text-sm font-semibold text-bark-700 mb-6">
            Net Operating Income
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DC" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B6B60" }} axisLine={{ stroke: "#D4D0C8" }} tickLine={false} />
              <YAxis tickFormatter={(v: number) => fmtCurrencyCompact(v)} tick={{ fontSize: 11, fill: "#6B6B60" }} axisLine={false} tickLine={false} domain={[0, (dataMax: number) => { const yr1Noi = chartData[0]?.noi ?? 1; return Math.min(dataMax, yr1Noi * 10); }]} label={{ value: "$", position: "insideTopLeft", offset: 8, style: { fontSize: 11, fill: "#6B6B60", fontWeight: 600 } }} />
              <Tooltip formatter={currencyTooltip} contentStyle={tooltipStyle} labelStyle={{ fontWeight: 600, color: "#2C2C28" }} itemStyle={{ color: "#3C3C36" }} />
              <Bar dataKey="noi" name="NOI" fill="#2D5016" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-grove-300 p-6 shadow-sm">
          <h3 className="font-display text-sm font-semibold text-bark-700 mb-6">
            Pro Forma
          </h3>
          {(() => {
            const hasDebtChart = ((deal?.property_data?.debt as Record<string, number> | undefined)?.loan_amount ?? 0) > 0;
            return (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DC" />
                  <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B6B60" }} axisLine={{ stroke: "#D4D0C8" }} tickLine={false} />
                  <YAxis tickFormatter={(v: number) => fmtCurrencyCompact(v)} tick={{ fontSize: 11, fill: "#6B6B60" }} axisLine={false} tickLine={false} label={{ value: "$", position: "insideTopLeft", offset: 8, style: { fontSize: 11, fill: "#6B6B60", fontWeight: 600 } }} />
                  <Tooltip formatter={currencyTooltip} contentStyle={tooltipStyle} labelStyle={{ fontWeight: 600, color: "#2C2C28" }} itemStyle={{ color: "#3C3C36" }} />
                  {hasDebtChart && <Legend wrapperStyle={{ fontSize: 11, color: "#6B6B60" }} />}
                  {hasDebtChart ? (
                    <>
                      <Line type="monotone" dataKey="cfBeforeDebt" name="Before Debt" stroke="#C8860A" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="cfAfterDebt" name="After Debt" stroke="#2D5016" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="debtService" name="Debt Service" stroke="#9B9B8E" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                    </>
                  ) : (
                    <Line type="monotone" dataKey="cfBeforeDebt" name="Cash Flow" stroke="#2D5016" strokeWidth={2} dot={false} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      </div>

      {/* Annual summary table */}
      <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
        <div className="px-6 py-5 border-b border-grove-200">
          <h3 className="font-display text-sm font-semibold text-bark-700">
            Annual Summary
          </h3>
        </div>
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Year</th>
              <th className="num">PGI</th>
              <th className="num">Vacancy</th>
              <th className="num">EGI</th>
              <th className="num">OpEx</th>
              <th className="num">NOI</th>
              <th className="num">NOI/SF</th>
              <th className="num">TI/LC</th>
              <th className="num">CF Before Debt</th>
              <th className="num">CF After Debt</th>
            </tr>
          </thead>
          <tbody>
            {cfs.map((cf: AnnualCashFlow) => (
              <tr key={cf.year}>
                <td className="font-medium text-bark-700">
                  Yr {cf.year}{" "}
                  <span className="text-[11px] text-bark-300">({cf.calendar_year})</span>
                </td>
                <td className="num">{fmtCurrencyAcct(cf.potential_gross_income)}</td>
                <td className="num text-bark-400">{fmtCurrencyAcct(-(cf.absorption_and_turnover_vacancy + cf.general_vacancy + cf.credit_loss))}</td>
                <td className="num">{fmtCurrencyAcct(cf.effective_gross_income)}</td>
                <td className="num">{fmtCurrencyAcct(cf.total_operating_expenses)}</td>
                <td className="num font-semibold text-forest-700">{fmtCurrencyAcct(cf.net_operating_income)}</td>
                <td className="num">{fmtPSF(cf.net_operating_income / tla)}</td>
                <td className="num text-bark-400">{fmtCurrencyAcct(-(cf.tenant_improvements + cf.leasing_commissions))}</td>
                <td className="num">{fmtCurrencyAcct(cf.cash_flow_before_debt)}</td>
                <td className="num">{fmtCurrencyAcct(cf.cash_flow_after_debt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Deal Score Card — after Annual Summary */}
      {deal.property_data && dealId && (
        <DealScoreCard dealId={dealId} />
      )}
    </div>
  );
}
